import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Modal, Row, Spinner } from 'react-bootstrap';
import { getSingleConference } from '../services/api';

const INITIAL_PHOTO_BATCH = 24;
const PHOTO_BATCH_SIZE = 24;

const ConferenceGallery = () => {
    const { id } = useParams();
    const [conference, setConference] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [showImageModal, setShowImageModal] = useState(false);
    const [visiblePhotoCount, setVisiblePhotoCount] = useState(INITIAL_PHOTO_BATCH);

    const baseUrl = process.env.REACT_APP_API_URL || '/api';

    useEffect(() => {
        setVisiblePhotoCount(INITIAL_PHOTO_BATCH);

        const fetchConference = async () => {
            try {
                setLoading(true);
                const response = await getSingleConference(id);
                setConference(response);
                setError(null);
            } catch (err) {
                console.error('Error fetching conference photos:', err);
                setError('Unable to load the conference gallery. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchConference();
        }
    }, [id]);

    const galleryPhotos = conference?.photos || [];
    const visiblePhotos = galleryPhotos.slice(0, visiblePhotoCount);

    const handleImageClick = (photo, index) => {
        setSelectedImage(photo);
        setSelectedImageIndex(index);
        setShowImageModal(true);
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImage(null);
        setSelectedImageIndex(0);
    };

    const handleNavigateImage = (direction) => {
        if (!galleryPhotos.length) return;

        const nextIndex = (selectedImageIndex + direction + galleryPhotos.length) % galleryPhotos.length;
        setSelectedImageIndex(nextIndex);
        setSelectedImage(galleryPhotos[nextIndex]);
    };

    const handleLoadMorePhotos = () => {
        setVisiblePhotoCount((currentCount) => Math.min(currentCount + PHOTO_BATCH_SIZE, galleryPhotos.length));
    };

    useEffect(() => {
        if (!showImageModal) return;

        const handleKeyDown = (event) => {
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                handleNavigateImage(1);
            }
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                handleNavigateImage(-1);
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                closeImageModal();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showImageModal, selectedImageIndex, galleryPhotos]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" role="status" variant="primary">
                    <span className="visually-hidden">Loading gallery...</span>
                </Spinner>
                <p className="mt-3">Loading conference gallery...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container py-5">
                <Alert variant="danger">
                    <Alert.Heading>Gallery unavailable</Alert.Heading>
                    <p>{error}</p>
                    <Link to={`/conference/${id}`} className="btn btn-outline-danger">
                        Back to conference
                    </Link>
                </Alert>
            </div>
        );
    }

    if (!conference) {
        return null;
    }

    return (
        <div className="py-5" style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #eef4ff 100%)' }}>
            <div className="container">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
                    <div>
                        <Link to={`/conference/${id}`} className="btn btn-outline-primary btn-sm mb-3">
                            <i className="bi bi-arrow-left me-2"></i>
                            Back to conference
                        </Link>
                        <h1 className="h3 mb-2">Conference Gallery</h1>
                        <p className="text-muted mb-0">{conference.title}</p>
                    </div>
                    <div className="text-md-end">
                        <Badge bg="primary" className="me-2">{conference.photos?.length || 0} photos</Badge>
                        {conference.location && <Badge bg="secondary">{conference.location}</Badge>}
                    </div>
                </div>

                {conference.photos && conference.photos.length > 0 ? (
                    <Row className="g-4">
                        {visiblePhotos.map((photo, index) => (
                            <Col key={`${photo.filename}-${index}`} md={6} lg={4} xl={3}>
                                <Card className="h-100 shadow-sm border-0 overflow-hidden">
                                    <div
                                        className="position-relative"
                                        style={{ cursor: 'pointer', aspectRatio: '4 / 3' }}
                                        onClick={() => handleImageClick(photo, index)}
                                    >
                                        <img
                                            src={`${baseUrl}/conferences/photos/${photo.filename}`}
                                            alt={photo.originalName || `Conference photo ${index + 1}`}
                                            className="w-100 h-100"
                                                style={{ objectFit: 'cover' }}
                                                loading="lazy"
                                                decoding="async"
                                        />
                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                            style={{ background: 'rgba(0,0,0,0.25)' }}>
                                            <i className="bi bi-zoom-in text-white fs-3"></i>
                                        </div>
                                    </div>
                                    <Card.Body>
                                        <Card.Title className="h6 mb-2 text-truncate">
                                            {photo.originalName || `Photo ${index + 1}`}
                                        </Card.Title>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleImageClick(photo, index)}
                                        >
                                            <i className="bi bi-eye me-2"></i>
                                            View full image
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                ) : (
                    <Alert variant="info">
                        <i className="bi bi-images me-2"></i>
                        No photos are available for this conference yet.
                    </Alert>
                )}

                {galleryPhotos.length > visiblePhotoCount && (
                    <div className="text-center mt-4">
                        <Button variant="outline-primary" onClick={handleLoadMorePhotos}>
                            <i className="bi bi-plus-lg me-2"></i>
                            Load more photos ({Math.min(visiblePhotoCount + PHOTO_BATCH_SIZE, galleryPhotos.length)} of {galleryPhotos.length})
                        </Button>
                    </div>
                )}
            </div>

            <Modal show={showImageModal} onHide={closeImageModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedImage?.originalName || 'Conference photo'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0 text-center bg-dark">
                    {selectedImage && (
                        <img
                            src={`${baseUrl}/conferences/photos/${selectedImage.filename}`}
                            alt={selectedImage.originalName || 'Conference photo'}
                            className="img-fluid"
                            style={{ maxHeight: '80vh', width: 'auto', maxWidth: '100%' }}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-between">
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleNavigateImage(-1)}
                            disabled={galleryPhotos.length <= 1}
                        >
                            <i className="bi bi-chevron-left me-1"></i>
                            Previous
                        </Button>
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleNavigateImage(1)}
                            disabled={galleryPhotos.length <= 1}
                        >
                            Next
                            <i className="bi bi-chevron-right ms-1"></i>
                        </Button>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">
                            {galleryPhotos.length > 0 ? `${selectedImageIndex + 1} / ${galleryPhotos.length}` : '0 / 0'}
                        </small>
                        <Button variant="secondary" size="sm" onClick={closeImageModal}>
                            Close
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ConferenceGallery;
